type TranslateFn = (key: string) => string;

type StructuredLimitError = {
  code: string | null;
  message: string | null;
  details: Record<string, unknown> | null;
};

function normalizeStructuredDetails(
  value: unknown,
): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function normalizeStructuredPayload(
  payload: unknown,
): StructuredLimitError | null {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }

  const code =
    typeof (payload as any).code === "string" &&
    (payload as any).code.trim().length > 0
      ? (payload as any).code.trim()
      : null;
  const message =
    typeof (payload as any).message === "string" &&
    (payload as any).message.trim().length > 0
      ? (payload as any).message.trim()
      : null;
  const details = normalizeStructuredDetails((payload as any).details);

  if (!code && !message) {
    return null;
  }

  return { code, message, details };
}

export function parseStructuredLimitError(
  error: unknown,
): StructuredLimitError | null {
  const guardrailCode =
    typeof (error as any)?.guardrailCode === "string" &&
    (error as any).guardrailCode.trim().length > 0
      ? (error as any).guardrailCode.trim()
      : null;

  if (guardrailCode) {
    const directPayload = normalizeStructuredPayload(error);
    return {
      code: guardrailCode,
      message: directPayload?.message ?? null,
      details: directPayload?.details ?? null,
    };
  }

  const directPayload = normalizeStructuredPayload(error);
  if (directPayload) {
    return directPayload;
  }

  const candidates = [
    (error as any)?.errors?.[0]?.message,
    (error as any)?.message,
  ];

  for (const candidate of candidates) {
    if (typeof candidate !== "string" || !candidate.trim()) {
      continue;
    }

    try {
      const parsedCandidate = JSON.parse(candidate);
      const parsedPayload = normalizeStructuredPayload(parsedCandidate);
      if (parsedPayload) {
        return parsedPayload;
      }
    } catch {
      return {
        code: null,
        message: candidate.trim(),
        details: null,
      };
    }
  }

  return null;
}

function getStructuredLimitLocaleKey(
  code: string | null,
  details: Record<string, unknown> | null,
): string | null {
  switch (code) {
    case "max_recipients_exceeded":
      return "message.errorMessage.maxRecipientsPerMessageReached";
    case "max_attachments_exceeded":
      return "message.errorMessage.maxAttachmentsPerMessageReached";
    case "max_total_attachment_size_exceeded":
      return "message.errorMessage.maxTotalAttachmentSizePerMessageReached";
    case "storage_limit_exceeded":
      return "file.errorMessage.storageLimitReached";
    case "plan_attachment_count_reached":
      return "file.errorMessage.planLimitReached";
    case "invalid_extension":
      return "file.errorMessage.invalidExtension";
    case "invalid_file_category":
      return "file.errorMessage.invalidFileCategory";
    case "file_too_large":
      return details?.category === "video"
        ? "file.errorMessage.videoFileTooLarge"
        : "file.errorMessage.fileTooLarge";
    case "attachment_state_conflict":
      return "file.errorMessage.attachmentStateConflict";
    case "file_upload_failed":
      return "file.errorMessage.fileUploadFailed";
    case "invalid_attachment_upload":
      return "file.errorMessage.fileUploadFailed";
    case "plan_recipient_count_reached":
      return "recipient.errorMessage.planLimitReached";
    case "recipient_still_associated":
      return "recipient.onScreenMessage.deleteRecipientBlocked";
    case "attachment_still_associated":
      return "file.onScreenMessage.deleteFileBlocked";
    default:
      return null;
  }
}

export function getLocalizedStructuredPlatformLimitErrorMessage(
  t: TranslateFn,
  error: unknown,
): string | null {
  const parsedError = parseStructuredLimitError(error);
  if (!parsedError) {
    return null;
  }

  const localeKey = getStructuredLimitLocaleKey(
    parsedError.code,
    parsedError.details,
  );

  return localeKey ? t(localeKey) : null;
}

export function getLocalizedPlatformLimitErrorMessage(
  t: TranslateFn,
  error: unknown,
  fallbackMessage: string,
): string {
  const parsedError = parseStructuredLimitError(error);
  if (!parsedError) {
    return fallbackMessage;
  }

  const localeKey = getStructuredLimitLocaleKey(
    parsedError.code,
    parsedError.details,
  );

  if (localeKey) {
    return t(localeKey);
  }

  return parsedError.message ?? fallbackMessage;
}
